import type {
  AllocationLoadStatus,
  OrgAllocationAssignment,
  OrgAllocationTargetRoster,
  OrgAllocationView,
  OrgPersonAllocationLoad,
} from './OrgAllocationService';

export interface OrgAllocationCanvasModelOptions {
  readonly maxPeople?: number;
  readonly maxTargets?: number;
}

export interface OrgAllocationCanvasPersonNode {
  readonly id: string;
  readonly label: string;
  readonly status: AllocationLoadStatus;
  readonly totalAllocation: number;
  readonly assignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
}

export interface OrgAllocationCanvasTargetNode {
  readonly id: string;
  readonly label: string;
  readonly targetKind: string;
  readonly totalAllocation: number;
  readonly peopleCount: number;
  readonly hasUnspecifiedAllocation: boolean;
}

export interface OrgAllocationCanvasEdge {
  readonly id: string;
  readonly personExternalId: string;
  readonly targetExternalId: string;
  readonly allocation: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly assignmentCount: number;
}

export interface OrgAllocationCanvasModel {
  readonly people: readonly OrgAllocationCanvasPersonNode[];
  readonly targets: readonly OrgAllocationCanvasTargetNode[];
  readonly edges: readonly OrgAllocationCanvasEdge[];
  readonly hiddenPeopleCount: number;
  readonly hiddenTargetCount: number;
  readonly hiddenAssignmentCount: number;
}

const DEFAULT_MAX_PEOPLE = 6;
const DEFAULT_MAX_TARGETS = 6;

export function buildOrgAllocationCanvasModel(
  view: OrgAllocationView,
  options: OrgAllocationCanvasModelOptions = {},
): OrgAllocationCanvasModel {
  const maxPeople = boundedLimit(options.maxPeople, DEFAULT_MAX_PEOPLE);
  const maxTargets = boundedLimit(options.maxTargets, DEFAULT_MAX_TARGETS);
  const selectedPeople = prioritizePeopleForCanvas(view.people).slice(0, maxPeople);
  const selectedPersonIds = new Set(selectedPeople.map((person) => person.personExternalId));
  const selectedTargets = prioritizeTargetsForCanvas(view.targets, selectedPersonIds).slice(0, maxTargets);
  const selectedTargetIds = new Set(selectedTargets.map((target) => target.targetExternalId));
  const edgeGroups = new Map<string, OrgAllocationAssignment[]>();
  let hiddenAssignmentCount = 0;

  for (const assignment of view.assignments) {
    if (!selectedPersonIds.has(assignment.personExternalId) ||
      !selectedTargetIds.has(assignment.targetExternalId)) {
      hiddenAssignmentCount += 1;
      continue;
    }

    const key = `${assignment.personExternalId}::${assignment.targetExternalId}`;
    const rows = edgeGroups.get(key) ?? [];
    rows.push(assignment);
    edgeGroups.set(key, rows);
  }

  return {
    people: selectedPeople.map(toPersonNode),
    targets: selectedTargets.map(toTargetNode),
    edges: Array.from(edgeGroups.entries())
      .map(([id, assignments]) => toEdge(id, assignments))
      .sort((a, b) => {
        const byPerson = a.personExternalId.localeCompare(b.personExternalId);
        if (byPerson !== 0) return byPerson;
        return a.targetExternalId.localeCompare(b.targetExternalId);
      }),
    hiddenPeopleCount: Math.max(0, view.people.length - selectedPeople.length),
    hiddenTargetCount: Math.max(0, view.targets.length - selectedTargets.length),
    hiddenAssignmentCount,
  };
}

function toPersonNode(person: OrgPersonAllocationLoad): OrgAllocationCanvasPersonNode {
  return {
    id: person.personExternalId,
    label: person.personLabel,
    status: person.status,
    totalAllocation: person.totalAllocation,
    assignmentCount: person.assignmentCount,
    hasUnspecifiedAllocation: person.hasUnspecifiedAllocation,
  };
}

function toTargetNode(target: OrgAllocationTargetRoster): OrgAllocationCanvasTargetNode {
  return {
    id: target.targetExternalId,
    label: target.targetLabel,
    targetKind: target.targetKind,
    totalAllocation: target.totalAllocation,
    peopleCount: target.peopleCount,
    hasUnspecifiedAllocation: target.hasUnspecifiedAllocation,
  };
}

function toEdge(
  id: string,
  assignments: readonly OrgAllocationAssignment[],
): OrgAllocationCanvasEdge {
  const first = assignments[0];
  return {
    id,
    personExternalId: first?.personExternalId ?? '',
    targetExternalId: first?.targetExternalId ?? '',
    allocation: assignments.reduce((sum, assignment) => sum + (assignment.allocation ?? 0), 0),
    hasUnspecifiedAllocation: assignments.some((assignment) => assignment.hasUnspecifiedAllocation),
    assignmentCount: assignments.length,
  };
}

function prioritizePeopleForCanvas(
  people: readonly OrgPersonAllocationLoad[],
): readonly OrgPersonAllocationLoad[] {
  const priority: Record<AllocationLoadStatus, number> = {
    over_allocated: 0,
    unknown: 1,
    under_allocated: 2,
    fully_allocated: 3,
  };
  return [...people].sort((a, b) => {
    const byStatus = priority[a.status] - priority[b.status];
    if (byStatus !== 0) return byStatus;
    const byAllocation = b.totalAllocation - a.totalAllocation;
    if (byAllocation !== 0) return byAllocation;
    return a.personLabel.localeCompare(b.personLabel);
  });
}

function prioritizeTargetsForCanvas(
  targets: readonly OrgAllocationTargetRoster[],
  selectedPersonIds: ReadonlySet<string>,
): readonly OrgAllocationTargetRoster[] {
  return [...targets].sort((a, b) => {
    const byVisibleConnections =
      visibleConnectionCount(b, selectedPersonIds) - visibleConnectionCount(a, selectedPersonIds);
    if (byVisibleConnections !== 0) return byVisibleConnections;
    const byAllocation = b.totalAllocation - a.totalAllocation;
    if (byAllocation !== 0) return byAllocation;
    return a.targetLabel.localeCompare(b.targetLabel);
  });
}

function visibleConnectionCount(
  target: OrgAllocationTargetRoster,
  selectedPersonIds: ReadonlySet<string>,
): number {
  return target.assignments.filter((assignment) => selectedPersonIds.has(assignment.personExternalId)).length;
}

function boundedLimit(value: number | undefined, fallback: number): number {
  if (value == null || !Number.isFinite(value)) return fallback;
  return Math.max(1, Math.floor(value));
}
